/**
 * "Patient report available" notifications — sent to the REFERRING doctor when a lab,
 * imaging, nursing or physiotherapy center presses "Share with Patient" on an order that came
 * from this doctor's prescription.
 *
 * Two shapes reach the app and both must be recognised:
 *   - LIVE (socket): `notification_type` is the semantic type below.
 *   - PERSISTED (REST list): the notification service stores the CHANNEL as the type
 *     ("realtime"), so the semantic type is only available from `data.notification_type`
 *     (or, for rows sent before that field existed, from the payload shape).
 *
 * `data` carries: provider_type, patient_id, child_id, child_name, booking_id,
 * booking_number, report_status, document_ids, report_ids.
 */

export const PATIENT_REPORT_NOTIFICATION_TYPE = "DOCTOR_PATIENT_REPORT_SHARED";

/** The doctor reports-history endpoint labels nursing rows "nurse"; normalise to that. */
const PROVIDER_ALIASES = {
  lab: "lab",
  imaging: "imaging",
  nurse: "nurse",
  nursing: "nurse",
  physiotherapy: "physiotherapy",
  physio: "physiotherapy",
};

export const normalizeReportProvider = (provider) =>
  PROVIDER_ALIASES[String(provider || "").trim().toLowerCase()] || null;

const parseObject = (value) => {
  if (value && typeof value === "object") return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
};

const positiveIntOrNull = (value) => {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
};

export const isPatientReportNotification = (notification) => {
  if (!notification) return false;
  const data = parseObject(notification.data);
  const type = String(
    notification.notification_type || notification.notificationType || "",
  ).toUpperCase();
  const dataType = String(data.notification_type || "").toUpperCase();
  if (
    type === PATIENT_REPORT_NOTIFICATION_TYPE ||
    dataType === PATIENT_REPORT_NOTIFICATION_TYPE
  ) {
    return true;
  }
  // Rows persisted before `data.notification_type` was added: recognise the payload shape.
  return Boolean(
    positiveIntOrNull(data.patient_id) &&
      normalizeReportProvider(data.provider_type) &&
      (data.booking_id || data.booking_number) &&
      String(data.report_status || "").toUpperCase() === "SENT",
  );
};

/**
 * Where a report notification should take the doctor: the patient's profile, opened on the
 * Medical History tab (where the reports list lives), scoped to the child the order was for,
 * with the shared order's report(s) highlighted.
 *
 * Returns `{ to, state }` for `navigate(to, { state })`, or null when the notification does not
 * identify a patient. The child's NAME travels in router state only, never in the URL.
 */
export const getPatientReportNavigation = (notification) => {
  if (!isPatientReportNotification(notification)) return null;
  const data = parseObject(notification.data);
  const patientId = positiveIntOrNull(data.patient_id);
  if (!patientId) return null;

  const params = new URLSearchParams();
  params.set("patient_id", String(patientId));
  params.set("tab", "medical-history");
  const childId = positiveIntOrNull(data.child_id);
  if (childId) params.set("child_id", String(childId));
  const provider = normalizeReportProvider(data.provider_type);
  if (provider) params.set("provider", provider);
  if (data.booking_id != null && data.booking_id !== "") {
    params.set("booking_id", String(data.booking_id));
  }
  if (data.booking_number) params.set("booking_number", String(data.booking_number));

  return {
    to: `/patients-profile?${params.toString()}`,
    state: {
      reportNotification: {
        childName: childId ? data.child_name || null : null,
      },
    },
  };
};

/**
 * Read the deep link produced by `getPatientReportNavigation` back out of a location.
 * Returns null when the URL is not a report deep link.
 */
export const parsePatientReportDeepLink = (search, state) => {
  const params = new URLSearchParams(search || "");
  const patientId = positiveIntOrNull(params.get("patient_id"));
  if (!patientId) return null;
  const provider = normalizeReportProvider(params.get("provider"));
  const bookingId = params.get("booking_id") || null;
  const bookingNumber = params.get("booking_number") || null;
  return {
    patientId,
    childId: positiveIntOrNull(params.get("child_id")),
    childName: state?.reportNotification?.childName || null,
    openMedicalHistory: params.get("tab") === "medical-history",
    highlight:
      provider && (bookingId || bookingNumber)
        ? { provider, bookingId, bookingNumber }
        : null,
  };
};

/** Whether a reports-history row belongs to the highlighted (just shared) order. */
export const isHighlightedReportRow = (row, highlight) => {
  if (!row || !highlight) return false;
  if (normalizeReportProvider(row.provider_type) !== highlight.provider) {
    return false;
  }
  if (highlight.bookingId != null && row.booking_id != null) {
    return String(row.booking_id) === String(highlight.bookingId);
  }
  return Boolean(
    highlight.bookingNumber &&
      row.booking_number &&
      String(row.booking_number) === String(highlight.bookingNumber),
  );
};
